import { configureStore } from '@reduxjs/toolkit';
import aiAnalysisReducer from './slices/aiAnalysisSlice';
import transcriptionReducer from './slices/transcriptionSlice';
import translationReducer from './slices/translationSlice';
import translationDeliveryReducer from './slices/translationDeliverySlice';
import notesReducer from './slices/notesSlice';
import officerChatReducer from './slices/officerChatSlice';
import participantChatReducer from './slices/participantChatSlice';
import callInfoReducer from './slices/callInfoSlice';
import feedbackReducer from './slices/feedbackSlice';
import toolSharingReducer from './slices/toolSharingSlice';
import callOwnershipReducer from './slices/callOwnershipSlice';
import orchestratorTreeReducer from './slices/orchestratorTreeSlice';
import notificationsReducer from './slices/notificationsSlice';
import contextReducer from './slices/contextSlice';
import calendarReducer from './slices/calendarSlice';
import mapSnapshotReducer from './slices/mapSnapshotSlice';
import officerClusterReducer from './slices/officerClusterSlice';
import treeClusterReducer from './slices/treeClusterSlice';
import dynamicGroupingReducer from './slices/dynamicGroupingSlice';
import { autoDeduplicationMiddleware } from './middleware/autoDeduplicationMiddleware';
import { textTraceMiddleware } from './middleware/textTraceMiddleware';

export const store = configureStore({
  reducer: {
    aiAnalysis: aiAnalysisReducer,
    transcription: transcriptionReducer,
    // Translation is a SEPARATE slice on purpose: the source reducer above keys
    // on segment/speaker identity with a text-similarity fallback, and routing
    // translated text through it would make a translation and its source collide
    // as duplicates. See `slices/translationSlice.ts`.
    translation: translationReducer,
    // Per-utterance delivery traffic, deliberately NOT a field on `translation`:
    // that slice's discipline is fencing leased, controller-authoritative
    // snapshots against regression, which is the wrong machinery for an
    // ephemeral counter. See `slices/translationDeliverySlice.ts`.
    translationDelivery: translationDeliveryReducer,
    notes: notesReducer,
    officerChat: officerChatReducer,
    participantChat: participantChatReducer,
    callInfo: callInfoReducer,
    feedback: feedbackReducer,
    toolSharing: toolSharingReducer,
    callOwnership: callOwnershipReducer,
    orchestratorTree: orchestratorTreeReducer,
    notifications: notificationsReducer,
    context: contextReducer,
    calendar: calendarReducer,
    mapSnapshot: mapSnapshotReducer,
    officerCluster: officerClusterReducer,
    treeCluster: treeClusterReducer,
    dynamicGrouping: dynamicGroupingReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Allow non-serializable values for thunks
      serializableCheck: {
        ignoredActions: ['aiAnalysis/deduplicateData/pending', 'aiAnalysis/deduplicateData/fulfilled'],
      },
    }).concat(autoDeduplicationMiddleware, textTraceMiddleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
